import { ChangeDetectionStrategy, Component, DOCUMENT, Input, OnInit, ViewChild, inject } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { MatIconModule } from "@angular/material/icon";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { ActivatedRoute, Params, Router, RouterModule } from "@angular/router";
import { MsalService } from "@azure/msal-angular";
import { AuthenticationResult } from "@azure/msal-browser";
import { TranslateModule } from "@ngx-translate/core";
import { BehaviorSubject, distinctUntilChanged, firstValueFrom } from "rxjs";
import { DefaultComponent } from "src/app/default.component";
import { JWTError } from "src/classes/JWTError";
import { OTPError } from "src/classes/OTPError";
import { ROUTES_CONFIG } from "src/config/routes.config";
import { STORAGE } from "src/config/storage.config";
import { TooltipDirective } from "src/directives/tooltip.directive";
import { LOCALES } from "src/enums/locales";
import { ENVIRONMENT } from "src/environments/environment";
import { SessionLoginJWT } from "src/interfaces/session/session-login-jwt";
import { SessionLoginQR } from "src/interfaces/session/session-login-qr";
import { SessionLoginTraditional } from "src/interfaces/session/session-login-traditional";
import { SafeStylePipe } from "src/pipes/safe-style";
import { ApplicationService } from "src/services/application.service";
import { HttpService } from "src/services/http.service";
import { SessionService } from "src/services/session.service";
import { v4 as uuid } from "uuid";
import { PrefixJanComponent } from "../../global/prefixes/impl/prefix-jan/prefix-jan.component";
import { PrefixOmsComponent } from "../../global/prefixes/impl/prefix-oms/prefix-oms.component";
import { PrefixQrcComponent } from "../../global/prefixes/impl/prefix-qrc/prefix-qrc.component";
import { PrefixWacComponent } from "../../global/prefixes/impl/prefix-wac/prefix-wac.component";
import { TemplateCheckboxComponent } from "../../global/prefixes/templates/template-checkbox/template-checkbox.component";
import { TemplateTextComponent } from "../../global/prefixes/templates/template-text/template-text.component";
import { ErrorMessageComponent } from "../../global/snackbar/impl/error-message/error-message.component";
import { SnackbarService } from "../../global/snackbar/snackbar.service";
import { TimerComponent } from "../../global/timer/timer.component";
import { QRCodeResponse } from "./qrcode.interface";

@Component({
  selector: "app-login",
  imports: [
    PrefixOmsComponent,
    PrefixWacComponent,
    TranslateModule,
    RouterModule,
    PrefixQrcComponent,
    TimerComponent,
    MatIconModule,
    TooltipDirective,
    FormsModule,
    MatProgressSpinnerModule,
    SafeStylePipe,
    PrefixJanComponent,
  ],
  templateUrl: "./login.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: "./login.component.less",
})
export class LoginComponent extends DefaultComponent implements OnInit {
  public application: ApplicationService;
  private session: SessionService;
  private router: Router;
  private route: ActivatedRoute;
  private http: HttpService;
  private snackbar: SnackbarService;
  private msal: MsalService;
  private document = inject(DOCUMENT);

  public isLoading: boolean;
  public showQR: BehaviorSubject<boolean>;
  public showEmailLogin: BehaviorSubject<boolean>;
  public showOTP: BehaviorSubject<boolean>;
  public qrcode: string | null;
  public qrIntervalTime: number;
  public checkInterval: NodeJS.Timeout | undefined;
  public news: string | null;

  @Input()
  public showNews: boolean;

  public env: typeof ENVIRONMENT;

  @ViewChild("username")
  public username: TemplateTextComponent | null;

  @ViewChild("password")
  public password: TemplateTextComponent | null;

  @ViewChild("privateemail")
  public privateEmail: TemplateCheckboxComponent | null;

  @ViewChild("otp")
  public otp: TemplateTextComponent | null;

  private authorizeConfig: { url: string; requestwebapptoken: string } | null;

  public constructor() {
    super();
    this.application = inject(ApplicationService);
    this.session = inject(SessionService);
    this.router = inject(Router);
    this.route = inject(ActivatedRoute);
    this.http = inject(HttpService);
    this.snackbar = inject(SnackbarService);
    this.msal = inject(MsalService);

    this.isLoading = false;
    this.showQR = new BehaviorSubject(false);
    this.showEmailLogin = new BehaviorSubject(false);
    this.showOTP = new BehaviorSubject(false);
    this.qrcode = null;
    this.qrIntervalTime = ENVIRONMENT.QR_REFRESH_RATE;
    this.checkInterval = undefined;

    this.env = ENVIRONMENT;
    this.username = null;
    this.password = null;
    this.privateEmail = null;
    this.otp = null;

    this.showNews = true;
    this.news = null;
    this.authorizeConfig = null;
  }

  public async ngOnInit(): Promise<void> {
    this.addSubscription(
      this.showQR.pipe(distinctUntilChanged()).subscribe((value) => {
        if (value) this.generateQR();
        this.toggleCheck(value);
      }),
      this.route.params.subscribe(async (params: Params) => {
        const id = params["id"];
        if (id) {
          switch (id) {
            case "sso":
              this.handleSSO();
              break;
            case "saml":
              this.handleSAML();
              break;

            default: {
              const deviceid = sessionStorage.getItem(STORAGE.REQUESTWEBAPPTOKEN_KEY) || undefined;
              sessionStorage.removeItem(STORAGE.REQUESTWEBAPPTOKEN_KEY);

              await this.handleLogin({
                ffwdrequest: id,
                permalink: this.route.snapshot.queryParams["permalink"] ?? undefined,
                requestwebapptoken: this.authorizeConfig?.requestwebapptoken ?? undefined,
                deviceid,
              });
            }
          }
        }
      }),
    );

    if (this.showNews) {
      this.news =
        (
          await this.http.retrieve<[{ getDataAsHTML: string; getDataAsJSON: string }]>(ROUTES_CONFIG.unsecureurl, {
            datatypeid: "maintenancemessage",
          })
        ).shift()?.getDataAsHTML || null;
    }

    const _route = this.route.snapshot;
    if (_route.routeConfig?.path === "authorize") {
      this.authorizeConfig = {
        requestwebapptoken: _route.queryParams["requestwebapptoken"],
        url: _route.queryParams["url"],
      };
    }
  }

  /**
   * Set locale of user
   * @param language
   */
  public setLocale(language: "EN" | "NL"): void {
    this.application.locale.next(LOCALES[language]);
  }

  /**
   * Handles session login
   */
  public async login(): Promise<void> {
    const usernameField = this.username;
    const passwordField = this.password;

    if (usernameField && passwordField) {
      const username = usernameField.value;
      const password = passwordField.value;

      this.isLoading = true;

      try {
        if (username && password) {
          const permalink = this.route.snapshot.queryParams["permalink"];

          await this.handleLogin({
            cdjID: username,
            wacPassword: password,
            RoleID: localStorage.getItem(STORAGE.ROLE_ID),
            WorkspaceID: localStorage.getItem(STORAGE.WORKSPACE_ID),
            permalink: permalink ?? undefined,
            requestwebapptoken: this.authorizeConfig?.requestwebapptoken ?? undefined,
          });
        } else {
          throw new Error("Invalid form");
        }
      } catch (err) {
        console.error(err);
      }

      this.isLoading = false;
    } else {
      throw new Error("Could not find username or password References");
    }
  }

  /**
   * Toggle qr-code view
   */
  public toggleQR(): void {
    this.showQR.next(!this.showQR.value);
  }

  public toggleEmailLogin(): void {
    this.showEmailLogin.next(!this.showEmailLogin.value);
  }

  /**
   * Generate a new qr code
   */
  public async generateQR(): Promise<void> {
    try {
      const response = await this.http.retrieve<QRCodeResponse[]>(ROUTES_CONFIG.unsecureurl, {
        datatypeid: "getloginQR",
      });
      const qrcode = response[0].getDataAsHTML;

      if (qrcode) {
        this.qrcode = qrcode;
      } else {
        throw new Error("Invalid QRCode generated.");
      }
    } catch (e) {
      this.snackbar.open<string>(ErrorMessageComponent, "ERRORS.INVALID_QR");
      this.showQR.next(false);
      this.qrcode = null;
    }
  }

  public async loginOTP(): Promise<void> {
    try {
      this.isLoading = true;
      await this.session.checkOTP(this.otp?.value || "", this.route.snapshot.queryParams["permalink"] ?? undefined);
      if ((await firstValueFrom(this.session.authenticated)) == true) {
        if (this.authorizeConfig) {
          this.session.disconnect();
          this.document.location = `${this.authorizeConfig.url}#code=${this.session.webapptoken.value}`;
        } else {
          this.toggleCheck(false);
          const permalink = this.session.permalink.value;
          const contentid = this.route.snapshot.queryParams["contentid"];
          await this.router.navigate(permalink || contentid ? ["/app/content", permalink ?? contentid] : ["/app/home"]);
        }
      }
    } catch (error) {
      if (error instanceof OTPError) {
        console.log("OTP was incorrect");
      } else {
        console.error(error);
      }
    } finally {
      this.isLoading = false;
    }
  }

  public async loginEmail(): Promise<void> {
    if (this.privateEmail && this.username) {
      this.isLoading = true;
      const check = this.privateEmail.value ?? false;
      const username = this.username.value;

      if (!username) throw new Error("Invalid username or check");

      try {
        await this.session.loginEmail({
          usernameoremail: username,
          privateemail: check,
        });
      } catch (e) {
        this.isLoading = false;
        console.error("[RECOVER ERROR]", e);
      } finally {
        this.isLoading = false;
      }
    } else {
      throw new Error("Undefined username or check control");
    }
  }

  /**
   * Enable/disable interval to check qr-code login
   * @param activate
   */
  private toggleCheck(activate: boolean): void {
    if (activate) {
      this.checkInterval = setInterval(() => {
        this.checkQR().catch((e) => {
          if (ENVIRONMENT.DEBUG) console.warn(e);
        });
      }, ENVIRONMENT.QR_CHECK_RATE);
    } else {
      clearInterval(this.checkInterval);
      this.checkInterval = undefined;
    }
  }

  /**
   * Check if qrcode has been scanned and then login
   */
  private async checkQR(): Promise<void> {
    const response = await this.http.retrieve<QRCodeResponse[]>(ROUTES_CONFIG.unsecureurl, {
      datatypeid: "checkloginQR",
      qr: this.qrcode,
    });

    const request = response[0].getDataAsHTML;

    if (request) {
      await this.session.connect({
        ffwdrequest: request,
      });
      if ((await firstValueFrom(this.session.authenticated)) == true) {
        this.toggleCheck(false);
        await this.router.navigate(["app/home"]);
      }
    } else {
      throw new Error("Undefined ffwdrequest in login");
    }
  }

  /**
   * Handle post login request
   * @param data
   */
  private async handleLogin(data: SessionLoginTraditional | SessionLoginQR | SessionLoginJWT, url?: string): Promise<void> {
    try {
      await this.session.connect(data, url);
      if ((await firstValueFrom(this.session.authenticated)) == true) {
        if (this.authorizeConfig) {
          this.session.disconnect();
          this.document.location = `${this.authorizeConfig.url}#code=${this.session.webapptoken.value}`;
        } else {
          this.toggleCheck(false);
          const permalink = this.session.permalink.value;
          const contentid = this.route.snapshot.queryParams["contentid"];
          await this.router.navigate(permalink || contentid ? ["/app/content", permalink ?? contentid] : ["/app/home"]);
        }
      }
    } catch (error) {
      if (error instanceof OTPError) {
        this.showOTP.next(true);
      } else if (error instanceof JWTError) {
        this.snackbar.open<string>(ErrorMessageComponent, "LOGIN.ERRORS.JWT");
        this.router.navigate(["/"]);
      } else {
        console.error(error);
      }
    }
  }

  private handleSSO(): void {
    if (!ENVIRONMENT.MSAL) {
      console.error("SSO is not enabled for your organisation.");
      this.router.navigate(["/login"]);
      return;
    }
    this.isLoading = true;

    const scopes = ["openid"];

    this.addSubscription(
      this.msal.handleRedirectObservable().subscribe({
        next: async (initialResult) => {
          let result: AuthenticationResult | null = null;

          if (initialResult) {
            console.log("Initial Auth Result");
            result = initialResult;
          }

          if (!result) {
            const account = this.msal.instance.getActiveAccount() ?? this.msal.instance.getAllAccounts()[0] ?? undefined;
            console.log("Initial redirect result was not present, attempting acquireTokenSilent. Account:", !!account);
            if (account) {
              try {
                const silentResult = await firstValueFrom(this.msal.acquireTokenSilent({ scopes, account }));
                if (silentResult) {
                  result = silentResult;
                }
              } catch (error) {
                console.error("Error in 'acquireTokenSilent' =>", error);
                await this.msal.instance.clearCache();
                this.msal.loginRedirect();
              }
            } else {
              await this.msal.instance.clearCache();
              console.log("Unable to find account, attempting loginRedirect.");
              this.msal.loginRedirect();
            }
          }
          if (result) {
            try {
              await this.handleLogin(
                {
                  jwt: result.idToken,
                  permalink: this.route.snapshot.queryParams["permalink"] ?? undefined,
                  requestwebapptoken: this.authorizeConfig?.requestwebapptoken ?? undefined,
                },
                ROUTES_CONFIG.loginJwtUrl,
              );
            } finally {
              this.isLoading = false;
            }
          }
        },
        error: async (e) => {
          console.error("Unable to find account, attempting loginRedirect. Error:", e);
          await this.msal.instance.clearCache();
          this.msal.loginRedirect();
        },
      }),
    );
  }

  private async handleSAML(): Promise<void> {
    this.isLoading = true;
    const requestwebapptoken = uuid();
    sessionStorage.setItem(STORAGE.REQUESTWEBAPPTOKEN_KEY, requestwebapptoken);

    const response = await this.http.send<{ idpURL: string; relayState: string; samlrequest: string }>(ROUTES_CONFIG.createsamlrequesturl, {
      requestwebapptoken,
    });

    const form = document.createElement("form");
    form.method = "POST";
    form.action = response.idpURL;

    const relayStateInput = document.createElement("input");
    relayStateInput.type = "hidden";
    relayStateInput.name = "RelayState";
    relayStateInput.value = response.relayState;
    form.appendChild(relayStateInput);

    const samlRequestInput = document.createElement("input");
    samlRequestInput.type = "hidden";
    samlRequestInput.name = "SAMLRequest";
    samlRequestInput.value = response.samlrequest;
    form.appendChild(samlRequestInput);

    document.body.appendChild(form);

    form.submit();
  }
}
